import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BUSINESS_DAYS, type BusinessDay, type FormField as ProfileFormField } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Input, Select, Checkbox, Textarea } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

interface ServiceProfile {
  id: string;
  slug: string;
  name: string;
  slot_duration_minutes: number;
  business_hours: Partial<Record<BusinessDay, { open: string; close: string }>>;
  blackout_dates: string[];
  form_fields: ProfileFormField[];
  branding: { primary_color?: string; logo_url?: string; intro_text?: string };
  auto_confirm: boolean;
  has_browse_password: boolean;
  redirect_url: string | null;
  email_account_id: string | null;
  notification_emails: string[];
  status: 'active' | 'disabled';
}

interface EmailAccount {
  id: string;
  display_name: string;
  from_email: string;
}

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');

export function EditServiceProfilePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [banner, setBanner] = useState<string | null>(null);
  const [form, setForm] = useState<ServiceProfile | null>(null);
  const [browsePassword, setBrowsePassword] = useState<string>('');
  const [clearPassword, setClearPassword] = useState(false);
  const [notifyEmailsText, setNotifyEmailsText] = useState('');
  const [blackoutsText, setBlackoutsText] = useState('');

  const q = useQuery({
    queryKey: ['admin', 'service-profile', id],
    queryFn: () => api.get(`/v1/admin/service-profiles/${id}`) as Promise<ServiceProfile>,
    enabled: !!id,
  });
  const emailAccountsQ = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/v1/email-accounts') as Promise<{ data: EmailAccount[] }>,
  });

  useEffect(() => {
    if (q.data && !form) {
      setForm(q.data);
      setNotifyEmailsText((q.data.notification_emails ?? []).join(', '));
      setBlackoutsText((q.data.blackout_dates ?? []).join(', '));
    }
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: (payload: unknown) => api.put(`/v1/admin/service-profiles/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'service-profile', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'service-profiles'] });
      setBrowsePassword('');
      setClearPassword(false);
      setBanner('Saved.');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setBanner('Slug already in use');
        return;
      }
      const detail =
        err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body
          ? JSON.stringify((err.body as { detail: unknown }).detail)
          : (err as Error).message;
      setBanner(detail);
    },
  });

  if (q.isLoading || !form) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (q.error) return <FormBanner message={(q.error as Error).message} />;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBanner(null);

    const notification_emails = notifyEmailsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const blackout_dates = blackoutsText
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const payload: Record<string, unknown> = {
      slug: form.slug,
      name: form.name,
      slot_duration_minutes: form.slot_duration_minutes,
      business_hours: form.business_hours,
      blackout_dates,
      form_fields: form.form_fields,
      branding: stripEmpty(form.branding),
      auto_confirm: form.auto_confirm,
      notification_emails,
      status: form.status,
    };
    if (form.redirect_url) payload.redirect_url = form.redirect_url;
    if (form.email_account_id) payload.email_account_id = form.email_account_id;
    if (clearPassword) payload.browse_password = '';
    else if (browsePassword) payload.browse_password = browsePassword;

    save.mutate(payload);
  };

  const update = (patch: Partial<ServiceProfile>): void =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <div className="max-w-3xl space-y-4">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{form.name}</h1>
          <p className="text-sm text-ink-muted font-mono">/{form.slug}</p>
        </div>
        <a
          href={`${PATH_PREFIX_NORMALIZED}/booking/${form.slug}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button variant="secondary">Preview</Button>
        </a>
      </header>

      {banner && <FormBanner message={banner} />}

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader><CardTitle>Basic</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <FormField label="Name">
              <Input id={id} value={form.name} onChange={(e) => update({ name: e.target.value })} />
            </FormField>
            <FormField label="Slug">
              <Input id={id}
                value={form.slug}
                onChange={(e) => update({ slug: e.target.value.toLowerCase() })}
              />
            </FormField>
            <FormField label="Status">
              <Select id={id}
                value={form.status}
                onChange={(e) => update({ status: e.target.value as 'active' | 'disabled' })}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </Select>
            </FormField>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Schedule</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <FormField label="Slot duration (minutes)">
              <Input id={id}
                type="number"
                min={5}
                max={480}
                value={form.slot_duration_minutes}
                onChange={(e) => update({ slot_duration_minutes: Number.parseInt(e.target.value, 10) || 0 })}
              />
            </FormField>
            <div>
              <h4 className="text-sm font-medium mb-2">Business hours</h4>
              <div className="space-y-1">
                {BUSINESS_DAYS.map((day) => {
                  const cur = form.business_hours[day];
                  const enabled = !!cur;
                  return (
                    <div key={day} className="grid grid-cols-[100px_auto_1fr_1fr] gap-2 items-center">
                      <span className="text-sm capitalize">{day}</span>
                      <Checkbox
                        checked={enabled}
                        onChange={(e) => {
                          const bh = { ...form.business_hours };
                          if (e.target.checked) bh[day] = { open: '09:00', close: '17:00' };
                          else delete bh[day];
                          update({ business_hours: bh });
                        }}
                      />
                      <Input
                        type="time"
                        disabled={!enabled}
                        value={cur?.open ?? ''}
                        onChange={(e) =>
                          update({
                            business_hours: {
                              ...form.business_hours,
                              [day]: { open: e.target.value, close: cur?.close ?? '17:00' },
                            },
                          })
                        }
                      />
                      <Input
                        type="time"
                        disabled={!enabled}
                        value={cur?.close ?? ''}
                        onChange={(e) =>
                          update({
                            business_hours: {
                              ...form.business_hours,
                              [day]: { open: cur?.open ?? '09:00', close: e.target.value },
                            },
                          })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <FormField
              label="Blackout dates"
              hint="Comma-separated YYYY-MM-DD (holidays, vacation)."
            >
              <Textarea id={id}
                rows={2}
                value={blackoutsText}
                onChange={(e) => setBlackoutsText(e.target.value)}
                placeholder="2026-12-25, 2026-12-26"
              />
            </FormField>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Form fields</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                update({
                  form_fields: [
                    ...form.form_fields,
                    { key: `field_${form.form_fields.length + 1}`, label: 'New field', type: 'text', required: false },
                  ],
                })
              }
            >
              + Add field
            </Button>
          </CardHeader>
          <CardBody className="space-y-2">
            {form.form_fields.length === 0 && (
              <p className="text-sm text-ink-muted">
                No custom fields. Default: visitor enters name, phone, email.
              </p>
            )}
            {form.form_fields.map((f, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_120px_auto_auto] gap-2 items-center">
                <Input
                  value={f.label}
                  onChange={(e) => {
                    const next = [...form.form_fields];
                    next[i] = { ...next[i]!, label: e.target.value };
                    update({ form_fields: next });
                  }}
                  placeholder="Label"
                />
                <Input
                  value={f.key}
                  onChange={(e) => {
                    const next = [...form.form_fields];
                    next[i] = { ...next[i]!, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') };
                    update({ form_fields: next });
                  }}
                  placeholder="key"
                  className="font-mono text-sm"
                />
                <Select
                  value={f.type}
                  onChange={(e) => {
                    const next = [...form.form_fields];
                    next[i] = { ...next[i]!, type: e.target.value as ProfileFormField['type'] };
                    update({ form_fields: next });
                  }}
                >
                  <option value="text">text</option>
                  <option value="textarea">textarea</option>
                  <option value="select">select</option>
                  <option value="checkbox">checkbox</option>
                </Select>
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={f.required}
                    onChange={(e) => {
                      const next = [...form.form_fields];
                      next[i] = { ...next[i]!, required: e.target.checked };
                      update({ form_fields: next });
                    }}
                  />
                  required
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => update({ form_fields: form.form_fields.filter((_, j) => j !== i) })}
                >
                  ×
                </Button>
              </div>
            ))}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Branding</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <FormField label="Primary color" hint="#RRGGBB">
              <Input id={id}
                value={form.branding.primary_color ?? ''}
                onChange={(e) => update({ branding: { ...form.branding, primary_color: e.target.value || undefined } })}
                placeholder="#0066ff"
              />
            </FormField>
            <FormField label="Logo URL">
              <Input id={id}
                value={form.branding.logo_url ?? ''}
                onChange={(e) => update({ branding: { ...form.branding, logo_url: e.target.value || undefined } })}
                placeholder="https://example.com/logo.png"
              />
            </FormField>
            <FormField label="Intro text">
              <Textarea id={id}
                rows={3}
                value={form.branding.intro_text ?? ''}
                onChange={(e) => update({ branding: { ...form.branding, intro_text: e.target.value || undefined } })}
              />
            </FormField>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Confirmation</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.auto_confirm}
                onChange={(e) => update({ auto_confirm: e.target.checked })}
              />
              Auto-confirm new bookings (otherwise visitor bookings land in “pending approval”)
            </label>
            <FormField label="Redirect URL after booking">
              <Input id={id}
                value={form.redirect_url ?? ''}
                onChange={(e) => update({ redirect_url: e.target.value || null })}
                placeholder="https://example.com/thank-you"
              />
            </FormField>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Email</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <FormField label="Send confirmation from">
              <Select id={id}
                value={form.email_account_id ?? ''}
                onChange={(e) => update({ email_account_id: e.target.value || null })}
              >
                <option value="">— Use customer default —</option>
                {emailAccountsQ.data?.data.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name} ({a.from_email})
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Notify these addresses on each booking"
              hint="Comma-separated."
            >
              <Textarea id={id}
                rows={2}
                value={notifyEmailsText}
                onChange={(e) => setNotifyEmailsText(e.target.value)}
                placeholder="ops@example.com, sales@example.com"
              />
            </FormField>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>Security</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            {form.has_browse_password && !clearPassword && (
              <p className="text-xs text-ink-muted">
                A browse password is set. Leave blank to keep it; type a new one to replace it; or
                <button
                  type="button"
                  onClick={() => setClearPassword(true)}
                  className="ml-1 text-danger hover:underline"
                >
                  clear it
                </button>
                .
              </p>
            )}
            {clearPassword && (
              <p className="text-xs text-warning">
                Password will be cleared on save (page becomes public).{' '}
                <button
                  type="button"
                  onClick={() => setClearPassword(false)}
                  className="text-ink-muted hover:underline"
                >
                  undo
                </button>
              </p>
            )}
            <FormField
              label="Browse password"
              hint="Min 4 chars. Visitors must enter this before seeing slots."
            >
              <Input id={id}
                type="password"
                value={browsePassword}
                onChange={(e) => setBrowsePassword(e.target.value)}
                disabled={clearPassword}
                placeholder={form.has_browse_password ? '•••••• (set)' : 'unset (public)'}
              />
            </FormField>
          </CardBody>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/service-profiles')}>
            Back to list
          </Button>
        </div>
      </form>
    </div>
  );
}

function stripEmpty<T extends Record<string, unknown>>(o: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined && v !== '' && v !== null) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}
